import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClosingAnimationEnd } from '@hooks/useClosingAnimationEnd';
import type { Delivery, DeliveryBoardStatus, Supplier } from '@interfaces/ILogistics';
import { dayLabel } from './calendar.labels';
import { monthCellFit, type CalendarMode } from './calendar.logic';
import { CalendarChip } from './CalendarChip';
import { CalendarDayList } from './CalendarDayList';
import { orderTone } from '../deliveryTone';
import styles from './CalendarDayCell.module.css';

interface ICalendarDayCellProps {
  day: string;
  orders: Delivery[];
  statuses: DeliveryBoardStatus[];
  suppliers: Supplier[];
  tz: string;
  /** The site's day, which decides lateness. */
  today: string;
  mode: CalendarMode;
  /** How many chip rows the grid's cells hold: a month cell lists what fits and counts the rest. */
  cellRows: number;
  outside: boolean;
  isSelected: boolean;
  /** Just jumped to from the side column. */
  flash: boolean;
  onFlashEnd: () => void;
  onSelect: (day: string) => void;
  onOpenDelivery: (delivery: Delivery) => void;
}

/** One day of the grid. The number is the keyboard's way to pick the day; a
 *  click anywhere in the cell picks it too, chips and "+N more" included, so
 *  the side column follows the order being opened. */
export function CalendarDayCell({ day, orders, statuses, suppliers, tz, today, mode, cellRows, outside, isSelected, flash, onFlashEnd, onSelect, onOpenDelivery }: ICalendarDayCellProps) {
  const { i18n } = useTranslation();
  const shown = mode === 'month' ? orders.slice(0, monthCellFit(cellRows, orders.length)) : orders;
  const hidden = orders.length - shown.length;
  const cellRef = useRef<HTMLDivElement>(null);
  const [listOpen, setListOpen] = useState(false);
  // Once everything fits (a taller window, fewer orders) the list goes with
  // "+N more", so it never reopens on its own when they overflow again.
  if (listOpen && hidden === 0) setListOpen(false);
  useClosingAnimationEnd(cellRef, flash, onFlashEnd);

  // Opening an order from the list closes the list, leaving its drawer.
  const chip = (delivery: Delivery) => (
    <CalendarChip
      key={delivery.ref}
      delivery={delivery}
      tone={orderTone(delivery, statuses, today)}
      suppliers={suppliers}
      tz={tz}
      mode={mode}
      onOpen={() => {
        setListOpen(false);
        onOpenDelivery(delivery);
      }}
    />
  );

  return (
    <div
      ref={cellRef}
      className={flash ? `${styles.day} ${styles.flash}` : styles.day}
      data-testid={`calendar-day-${day}`}
      data-outside={outside}
      data-today={day === today}
      data-selected={isSelected}
      onClick={() => onSelect(day)}
    >
      <button type="button" className={styles.number} aria-pressed={isSelected} aria-label={dayLabel(day, i18n.language)}>
        {Number(day.slice(8))}
      </button>
      {shown.map(chip)}
      {hidden > 0 ? (
        <CalendarDayList day={day} count={orders.length} hidden={hidden} anchor={cellRef} open={listOpen} onOpenChange={setListOpen}>
          {orders.map(chip)}
        </CalendarDayList>
      ) : null}
    </div>
  );
}
